import Label from 'Label';
import styles from 'MatchSelectCellStyle';
import {StyleSheet, View, Animated, Platform, LayoutChangeEvent} from 'react-native';
import {useEffect, useMemo, useRef, useCallback} from 'react';

interface AnimatedDiscountFeeProps {
  entryFee: number;
  discountObject:
    | {
        discount_percent: number;
        discount_end_time: string;
        discounted_cash_fee: number;
      }
    | undefined;
  entryFeeIcon: JSX.Element;
  discountedFeeIcon?: JSX.Element;
  shouldTriggerAnimation?: boolean;
  hasAnimated?: boolean;
  isZBCTournament?: boolean;
  zFee?: number;
}

const AnimatedDiscountFee: React.FC<AnimatedDiscountFeeProps> = ({
  entryFee,
  discountObject,
  entryFeeIcon,
  discountedFeeIcon,
  shouldTriggerAnimation = false,
  hasAnimated,
  isZBCTournament = false,
  zFee,
}) => {
  /**
   * Check if discount is applicable, if true increase the opacity of | from 0 to 1
   * Translate old entry fee to the left (x axis)
   * Animate opacity of gray | from 0 to 1
   * Animate opacity of discounted entry fee from 0 to 1 and give bounce effect 3 times
   */

  const diagonalCrossAnimate = useRef(new Animated.Value(1)).current;
  const discountedFeeAnimate = useRef(new Animated.Value(1)).current;
  const discountFeeOpacity = useRef(new Animated.Value(0)).current;
  const width = useRef(0);
  const animatedWidth = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (hasAnimated) {
      diagonalCrossAnimate.setValue(0);
      discountedFeeAnimate.setValue(1);
      discountFeeOpacity.setValue(1);
      animatedWidth.setValue(width.current);
      return;
    }
    if (shouldTriggerAnimation) {
      Animated.timing(diagonalCrossAnimate, {
        toValue: 0,
        duration: 1000,
        useNativeDriver: false,
      }).start(({finished}) => {
        if (finished) {
          Animated.timing(animatedWidth, {
            toValue: width.current,
            duration: 900,
            useNativeDriver: false,
          }).start(({finished}) => {
            if (finished) {
              // Create bounce animation sequence repeated 3 times
              const bounceSequence = Animated.sequence([
                Animated.timing(discountedFeeAnimate, {
                  toValue: 1.2,
                  duration: 700,
                  useNativeDriver: true,
                }),
                Animated.timing(discountedFeeAnimate, {
                  toValue: 1,
                  duration: 700,
                  useNativeDriver: true,
                }),
              ]);

              Animated.loop(bounceSequence, {iterations: 3}).start();

              Animated.timing(discountFeeOpacity, {
                toValue: 1,
                duration: 500,
                useNativeDriver: true,
              }).start();
            }
          });
        }
      });
    }

    // Cleanup animations on unmount
    return () => {
      diagonalCrossAnimate.stopAnimation();
      discountedFeeAnimate.stopAnimation();
      discountFeeOpacity.stopAnimation();
      animatedWidth.stopAnimation();
    };
  }, [shouldTriggerAnimation, hasAnimated]);

  const animateDiagonalCrossStyle = {
    transform: [
      {
        translateX: diagonalCrossAnimate.interpolate({
          inputRange: [0, 1],
          outputRange: [0, -40],
        }),
      },
      {
        translateY: diagonalCrossAnimate.interpolate({
          inputRange: [0, 1],
          outputRange: [0, -40],
        }),
      },
      {rotate: `${entryFee}`?.length === 1 ? '-40deg' : '-65deg'},
    ],
  };

  const animateDiscountStyle = {
    transform: [{scale: discountedFeeAnimate}],
  };

  const animatedOpacityStyle = {
    opacity: discountFeeOpacity,
  };

  const animatedWidthStyle = {
    width: animatedWidth,
  };

  const animatedEntryFeeStyle = {
    transform: [
      {
        scale: animatedWidth.interpolate({
          inputRange: [0, width.current],
          outputRange: [1, 0.7],
          extrapolate: 'clamp',
        }),
      },
    ],
  };

  const renderDiscount = useMemo(() => {
    if (isZBCTournament) {
      return zFee || 0;
    }
    return discountObject?.discount_percent === 100 ? 'FREE' : discountObject?.discounted_cash_fee;
  }, [
    discountObject?.discount_percent,
    discountObject?.discounted_cash_fee,
    isZBCTournament,
    zFee,
  ]);

  const dynamicFontSize = useMemo(() => {
    const discountValue = renderDiscount;
    if (discountValue === 'FREE') {
      return 16;
    } else {
      return 19;
    }
  }, [renderDiscount]);

  const handleLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const newWidth = e.nativeEvent.layout.width;
      // Only update width if significantly different to prevent layout thrashing during scroll
      if (Math.abs(width.current - newWidth) > 1) {
        width.current = newWidth;
        // If not animating, immediately set the animated width to show the discounted fee
        if (hasAnimated) {
          animatedWidth.setValue(newWidth);
        }
      }
    },
    [hasAnimated, animatedWidth]
  );

  return (
    <Animated.View style={st.flexSpaceBetween}>
      <View
        style={[
          isZBCTournament ? st.flexCenterNoSpacing : st.flexCenter,
          st.overflowHidden,
          Platform.OS !== 'web' && {height: 20},
        ]}
      >
        {entryFeeIcon}
        <View
          style={[
            isZBCTournament ? st.flexCenterNoSpacing : st.flexCenter,
            st.overflowHidden,
            isZBCTournament ? {justifyContent: 'center', alignItems: 'center', marginLeft: -2} : {},
          ]}
        >
          <Animated.Text
            allowFontScaling={false}
            style={[
              styles.entryFeePrizeBeforeDiscount,
              animatedEntryFeeStyle,
              isZBCTournament ? st.noMargin : {},
              isZBCTournament ? {textAlign: 'center'} : {},
            ]}
          >
            {entryFee}
          </Animated.Text>
          <Animated.Text
            allowFontScaling={false}
            style={[styles.slashedDiscountedFee, animateDiagonalCrossStyle]}
          >
            |
          </Animated.Text>
        </View>
      </View>
      <Animated.View
        style={[
          {
            height: 20,
            alignItems: 'flex-start',
            ...(Platform.OS === 'web' && {marginTop: -5}),
          },
          animatedWidthStyle,
        ]}
      >
        <Animated.View
          onLayout={handleLayout}
          style={[st.flexCenterNoSpacing, st.overflowVisible, animatedOpacityStyle]}
        >
          <Label
            style={[styles.pipeSeparator, isZBCTournament ? {marginLeft: 0, marginRight: 0} : {}]}
          >
            |
          </Label>
          {discountedFeeIcon && (
            <View style={st.discountedFeeIconContainerNoSpacing}>{discountedFeeIcon}</View>
          )}
          <Animated.Text
            allowFontScaling={false}
            numberOfLines={1}
            ellipsizeMode='clip'
            style={[
              styles.discountedFee,
              animateDiscountStyle,
              {
                fontSize: dynamicFontSize,
                marginTop: Platform.OS === 'ios' ? -5 : 0,
              },
            ]}
          >
            {renderDiscount}
          </Animated.Text>
        </Animated.View>
      </Animated.View>
    </Animated.View>
  );
};

export default AnimatedDiscountFee;

const st = StyleSheet.create({
  flexCenter: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  flexCenterNoSpacing: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 0,
    margin: 0,
    padding: 0,
  },
  flexSpaceBetween: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  overflowHidden: {
    overflow: 'hidden',
  },
  overflowVisible: {
    overflow: 'visible',
    position: 'absolute',
  },
  discountedFeeIconContainer: {
    marginRight: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  discountedFeeIconContainerNoSpacing: {
    marginRight: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  noMargin: {
    margin: 0,
    marginLeft: 0,
    marginRight: 0,
    padding: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
});
